// PdfViewerDialog.tsx

import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  CircularProgress,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import pdfViewerApi from "@/services/pdfhtmlServicee";

interface PdfViewerDialogProps {
  quizName: string;
  paper: string;
  open: boolean;
  onClose: () => void;
  page: number;
  sentenceToHighlights: string[];
}

interface CharMap {
  rawIndex: number;
  nodeIndex: number;
  offsetInNode: number;
}

const normalizeForSearch = (str: string): string => {
  return str
    .toLowerCase()
    .replace(/（.*?）|\(.*?\)/g, "") // Remove bracket contents
    .replace(/[-–—]+/g, "") // Remove dashes
    .replace(/[；;：:，,。./\\_]/g, "") // Remove punctuation
    .replace(/\s+/g, "") // Remove whitespace
    .trim();
};

export const PdfViewerDialog: React.FC<PdfViewerDialogProps> = ({
  quizName,
  paper,
  open,
  onClose,
  page,
  sentenceToHighlights,
}) => {
  const { t } = useTranslation();
  const PREFIX = "components.pdfViewerDialog";

  const [htmlContent, setHtmlContent] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadPage = useCallback(async (quizNameParam: string, paperParam: string, pageNumber: number) => {
    try {
      setLoading(true);
      const targetPages = [pageNumber - 3, pageNumber - 2, pageNumber - 1, pageNumber, pageNumber + 1, pageNumber + 2].filter(
        (p) => p > 0,
      );

      const html = await pdfViewerApi.getPages({
        quizName: quizNameParam,
        paper: paperParam,
        pageNumbers: targetPages,
      });

      return html;
    } catch (error) {
      console.error("Failed to load PDF pages:", error);
      return "";
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    if (open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      loadPage(quizName, paper, page).then((html) => {
        if (isMounted) {
          setHtmlContent(html);
        }
      });
    } else {
      setHtmlContent("");
    }

    return () => {
      isMounted = false;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [open, quizName, paper, page, loadPage]);

  // Linear O(N) DOM Text Highlight Process
  const applyHighlights = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe || !iframe.contentDocument || !sentenceToHighlights.length) return;

    const doc = iframe.contentDocument;
    const win = iframe.contentWindow;
    if (!win) return;

    const selection = win.getSelection();
    if (selection) selection.removeAllRanges();

    // 1. Collect all DOM text nodes
    const textNodes: Node[] = [];
    const walk = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT, null);
    let currentNode = walk.nextNode();

    while (currentNode) {
      textNodes.push(currentNode);
      currentNode = walk.nextNode();
    }

    if (textNodes.length === 0) return;

    // 2. Build a single linear normalized string with an O(N) index mapping array
    let normalizedFullText = "";
    const charMap: CharMap[] = [];

    textNodes.forEach((node, nodeIndex) => {
      const rawText = node.nodeValue || "";
      for (let offset = 0; offset < rawText.length; offset++) {
        const char = rawText[offset];
        const normalizedChar = normalizeForSearch(char);
        if (normalizedChar.length > 0) {
          normalizedFullText += normalizedChar;
          charMap.push({
            rawIndex: charMap.length,
            nodeIndex,
            offsetInNode: offset,
          });
        }
      }
    });

    let highlightedCount = 0;

    // 3. Search and highlight targets
    sentenceToHighlights.forEach((sentence) => {
      const cleanSearchText = normalizeForSearch(sentence).replace(/^\.\.\.|\.\.\.$/g, "");
      if (!cleanSearchText) return;

      const matchIdx = normalizedFullText.indexOf(cleanSearchText);

      if (matchIdx !== -1 && charMap.length > 0) {
        const startMap = charMap[matchIdx];
        const endMap = charMap[matchIdx + cleanSearchText.length - 1];

        if (startMap && endMap && selection) {
          const startNode = textNodes[startMap.nodeIndex];
          const endNode = textNodes[endMap.nodeIndex];

          const range = doc.createRange();
          range.setStart(startNode, startMap.offsetInNode);
          range.setEnd(endNode, endMap.offsetInNode + 1);

          selection.addRange(range);

          try {
            doc.designMode = "on";
            doc.execCommand("BackColor", false, "#ffd54f");
            doc.designMode = "off";
          } catch (e) {
            console.warn("execCommand BackColor not supported:", e);
          }

          selection.removeAllRanges();
          highlightedCount++;

          if (highlightedCount === 1) {
            timerRef.current = setTimeout(() => {
              if (startNode.parentNode) {
                (startNode.parentNode as HTMLElement).scrollIntoView({
                  behavior: "smooth",
                  block: "center",
                });
              }
            }, 200);
          }
        }
      }
    });

    // Fallback scroll if no highlights matched
    if (highlightedCount === 0) {
      timerRef.current = setTimeout(() => {
        const container = doc.querySelector(".viewer-container");
        const scrollTarget = container || win;
        scrollTarget.scrollBy({
          top: win.innerHeight * 0.8,
          behavior: "smooth",
        });
      }, 200);
    }
  }, [sentenceToHighlights]);

  const handleIframeLoad = () => {
    applyHighlights();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      scroll="paper"
      slotProps={{
        paper: {
          sx: {
            height: "90vh",
            maxHeight: "90vh",
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          },
        },
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h6" component="div" sx={{ color: "text.primary" }}>
          {t(`${PREFIX}.title`)}
        </Typography>
        <IconButton aria-label={t(`${PREFIX}.close`)} onClick={onClose} sx={{ color: "text.secondary" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ backgroundColor: "background.default", p: 0, position: "relative", overflow: "hidden" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
            <CircularProgress />
          </Box>
        ) : (
          <iframe
            ref={iframeRef}
            srcDoc={htmlContent}
            title={t(`${PREFIX}.iframeTitle`)}
            onLoad={handleIframeLoad}
            style={{ width: "100%", height: "100%", border: "none", display: "block" }}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined" color="inherit">
          {t(`${PREFIX}.close`)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
